import {
  fetchTicketAttachments,
  getWorkflowFiveDetails,
  updateFileTicketByWorkflow4Validation12,
  updateFileTicketRemovedByMa,
  uploadTicketAttachment,
} from '@/api/maintenance-tickets-api'
import ImageIcon from '@/assets/icons/image-icon.png'
import PdfIcon from '@/assets/icons/pdf-icon.png'
import { useAlert } from '@/utilities/useAlert'
import { Delete } from '@mui/icons-material'
import CloseIcon from '@mui/icons-material/Close'
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  Snackbar,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import Button from '@mui/material/Button'
import { styled } from '@mui/material/styles'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs, { Dayjs } from 'dayjs'
import { FC, useEffect, useState } from 'react'
import useSWR from 'swr'

interface ApproveModalProps {
  open: boolean
  onClose: () => void
  onApprove: (
    ticketId: number,
    note: string,
    cocDate?: any,
    projectDate?: any,
    shouldShowUpdate?: any,
  ) => void
  data?: any
  mutate: () => Promise<any>
  onCloseViewTicket: () => void
  isNested?: boolean
}

const CustomTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderWidth: 2,
    },
    '&:hover fieldset': {
      borderWidth: 2,
    },
    '&.Mui-focused fieldset': {
      borderWidth: 2,
    },
  },
})

const ButtonGroup = styled(DialogActions)({
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: 20,
  marginBottom: 20,
  marginLeft: 20,
  marginRight: 25,
  gap: 8,
  flexDirection: 'row',
  '& > :not(style) ~ :not(style)': {
    marginLeft: 0,
  },
  '@media (max-width: 600px)': {
    flexDirection: 'column-reverse',
    marginLeft: 18,
    marginRight: 18,
    '& > *': {
      width: '100%',
      marginBottom: 8,
    },
    '& > :not(style) ~ :not(style)': {
      marginLeft: 0,
    },
  },
})

export const ApproveModal: FC<ApproveModalProps> = ({
  open,
  onClose,
  onApprove,
  data,
  mutate,
  onCloseViewTicket,
  isNested,
}) => {
  const [remarks, setRemarks] = useState('')
  const [projectDate, setProjectDate] = useState<Dayjs | null>(null)
  const [cocDate, setCocDate] = useState<Dayjs | null>(null)
  const [approvalDate, setApprovalDate] = useState<Dayjs | null>(null)
  const [isApproving, setIsApproving] = useState(false)

  const { handleAlertMessage, ...alertRest } = useAlert()

  const roleString = localStorage.getItem('role')
  const userRole = roleString ? JSON.parse(roleString) : ''

  const shouldFetchSignedDISAttachments =
    data?.currentLayerId === 14 &&
    userRole === 'Maintenance Admin' &&
    data?.workFlowId === 4

  const shouldFetchDISAttachments =
    data?.currentLayerId === 8 &&
    userRole === 'Maintenance Admin' &&
    data?.workFlowId === 4

  const getAllCOCTicketAttachments = async () => {
    try {
      const response = await fetchTicketAttachments(data.id, 'COC_QUOTATION')
      return response.data.data ?? []
    } catch (error) {
      console.error('Failed to fetch attachments:', error)
      throw error
    }
  }

  const getAllSignedDISTicketAttachments = async () => {
    try {
      const response = await fetchTicketAttachments(
        data.id,
        'SIGNED_DIS_QUOTATION',
      )
      return response.data.data
    } catch (error) {
      console.error('Failed to fetch attachments:', error)
      throw error
    }
  }

  const getAllDISTicketAttachments = async () => {
    try {
      const response = await fetchTicketAttachments(data.id, 'DIS_QUOTATION')
      return response.data.data
    } catch (error) {
      console.error('Failed to fetch attachments:', error)
      throw error
    }
  }

  const fetchWorkflowFiveDetails = async () => {
    try {
      const response = await getWorkflowFiveDetails(data.id)
      return response.data.data
    } catch (error) {
      console.error('Failed to fetch attachments:', error)
      throw error
    }
  }

  const getAllReplyTicketAttachments = async () => {
    try {
      const response = await fetchTicketAttachments(
        data.id,
        'ALL_EXCEPT_CONCERN',
      )
      return response.data.data
    } catch (error) {
      console.error('Failed to fetch attachments:', error)
      throw error
    }
  }

  const shouldFetchReplyAttachments =
    data?.currentLayerId === 4 &&
    userRole === 'Maintenance Admin' &&
    (data?.workFlowId === 1 || data?.workFlowId === 2 || data?.workFlowId === 5)

  const shouldFetchCOCAttachments =
    data?.currentLayerId === 10 ||
    data?.currentLayerId === 11 ||
    data?.currentLayerId === 12 ||
    data?.currentLayerId === 17 ||
    data?.currentLayerId === 18

  const { data: disAttachments, mutate: mutateDISAttachments } = useSWR(
    shouldFetchDISAttachments ? `/v1/attachment` : null,
    getAllDISTicketAttachments,
    { revalidateOnFocus: false },
  )

  const { data: signedDisAttachments, mutate: mutateSignedDISAttachments } =
    useSWR(
      shouldFetchSignedDISAttachments ? `/v1/attachment` : null,
      getAllSignedDISTicketAttachments,
      { revalidateOnFocus: false },
    )

  const { data: cocAttachments, mutate: mutateCOCAttachments } = useSWR(
    shouldFetchCOCAttachments ? `/v1/attachment` : null,
    getAllCOCTicketAttachments,
    { revalidateOnFocus: false },
  )

  const {
    data: approvalDateAndGatePass,
    mutate: mutateApprovalDateAndGatePass,
  } = useSWR(
    shouldFetchCOCAttachments || shouldFetchReplyAttachments
      ? `/v1/ticket/details`
      : null,
    fetchWorkflowFiveDetails,
    { revalidateOnFocus: false },
  )


  const shouldShowUpdate =
    userRole === 'Maintenance Admin' &&
  (
    ((data?.workFlowId === 1 || data?.workFlowId === 2) && data?.currentLayerId === 11) ||
    ((data?.workFlowId === 3 || data?.workFlowId === 4) && (data?.currentLayerId === 17 || data?.currentLayerId === 18)) ||
    (data?.workFlowId === 5 && data?.currentLayerId === 11)
  ) &&
  (
    !cocAttachments ||
    cocAttachments.length === 0 ||
    !(cocDate && projectDate)
  )

  const { data: replyAttachments, mutate: mutateReplyAttachments } = useSWR(
    shouldFetchReplyAttachments ? `/v1/attachment` : null,
    getAllReplyTicketAttachments,
    { revalidateOnFocus: false },
  )

  const handleUploadCOC = async (
    e: React.ChangeEvent<HTMLInputElement>,
    file: File,
  ) => {
    try {
      await uploadTicketAttachment(data.id, 'COC_QUOTATION', file)
      mutateCOCAttachments()
    } catch (error) {
      console.error('Failed to upload coc attachment:', error)
    } finally {
      e.target.value = ''
    }
  }

  

  const handleUploadReply = async (
    e: React.ChangeEvent<HTMLInputElement>,
    file: File,
  ) => {
    try {
      await uploadTicketAttachment(data.id, 'REPLY_LETTER_QUOTATION', file)
      mutateReplyAttachments()
    } catch (error) {
      console.error('Failed to upload reply attachment:', error)
    } finally {
      e.target.value = ''
    }
  }

  const handleUploadDIS = async (
    e: React.ChangeEvent<HTMLInputElement>,
    file: File,
  ) => {
    try {
      await uploadTicketAttachment(data.id, 'DIS_QUOTATION', file)
      mutateDISAttachments()
    } catch (error) {
      console.error('Failed to upload dis attachment:', error)
    } finally {
      e.target.value = ''
    }
  }

  const handleUploadSignedDIS = async (
    e: React.ChangeEvent<HTMLInputElement>,
    file: File,
  ) => {
    try {
      await uploadTicketAttachment(data.id, 'SIGNED_DIS_QUOTATION', file)
      mutateSignedDISAttachments()
    } catch (error) {
      console.error('Failed to upload dis attachment:', error)
    } finally {
      e.target.value = ''
    }
  }

  const handleRemoveReplyFile = async (idToRemove: number) => {
    if (!replyAttachments) return

    const remainingIds = replyAttachments
      .filter((file: any) => file.id !== idToRemove)
      .map((file: any) => file.id)

    try {
      await updateFileTicketRemovedByMa(
        data.id,
        parseFloat(data.amount),
        remainingIds,
      )
      mutateReplyAttachments()
    } catch (error) {
      console.error('Failed to remove file:', error)
    }
  }

  const handleRemoveFile = async (idToRemove: number) => {
    if (!cocAttachments) return

    const remainingIds = cocAttachments
      .filter((file: any) => file.id !== idToRemove)
      .map((file: any) => file.id)

    try {
      await updateFileTicketRemovedByMa(
        data.id,
        parseFloat(data.amount),
        remainingIds,
      )
      mutateCOCAttachments()
    } catch (error) {
      console.error('Failed to remove file:', error)
    }
  }

  const handleRemoveDISFile = async (idToRemove: number) => {
    if (!disAttachments) return

    const remainingIds = disAttachments
      .filter((file: any) => file.id !== idToRemove)
      .map((file: any) => file.id)

    try {
      await updateFileTicketRemovedByMa(
        data.id,
        parseFloat(data.amount),
        remainingIds,
      )
      mutateDISAttachments()
    } catch (error) {
      console.error('Failed to remove file:', error)
    }
  }

  const handleRemoveSignedDISFile = async (idToRemove: number) => {
    if (!signedDisAttachments) return

    const remainingIds = approvalDateAndGatePass?.attachments
      .filter((file: any) => file.id !== idToRemove)
      .map((file: any) => file.id)

    try {
      await updateFileTicketRemovedByMa(
        data.id,
        parseFloat(data.amount),
        remainingIds,
      )
      mutateSignedDISAttachments()
    } catch (error) {
      console.error('Failed to remove file:', error)
    }
  }

  console.log('data modal here ', data)

  const handleApproveWorkflow4Validation12 = async () => {
    if (isApproving) return // Prevent multiple calls
    
    if (approvalDate) {
      setIsApproving(true)
      try {
        await updateFileTicketByWorkflow4Validation12(
          data.id,
          data.amount,
          approvalDate.format('YYYY-MM-DD HH:mm:ss'),
        )
        mutate()
        onClose()
        onCloseViewTicket()
      } catch (error) {
        console.error('Failed to approve workflow:', error)
        handleAlertMessage('Failed to approve. Please try again.', 'error')
      } finally {
        setIsApproving(false)
      }
    } else {
      handleAlertMessage('Date of Approval is empty.', 'error')
    }
  }

  useEffect(() => {
    if (approvalDateAndGatePass) {
      setCocDate(
        approvalDateAndGatePass.dateCocReceived
          ? dayjs(approvalDateAndGatePass.dateCocReceived)
          : null,
      )
      setProjectDate(
        approvalDateAndGatePass.dateCompletion
          ? dayjs(approvalDateAndGatePass.dateCompletion)
          : null,
      )
    }
  }, [open, approvalDateAndGatePass])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth={!isNested}
      maxWidth={!isNested ? 'sm' : false}
      PaperProps={{
        sx: isNested ? { width: 'auto', maxWidth: 'unset', minWidth: 650 } : {},
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{ px: '2rem', pt: '1.5rem' }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              sx={{
                fontFamily: 'Gabarito',
                fontWeight: 500,
                fontSize: '22px',
                color: '#505470',
              }}
            >
              Approve Ticket
            </Typography>
            <IconButton
              size="small"
              aria-label="close"
              sx={{
                color: '#505470',
                width: 20,
                height: 20,
              }}
              onClick={onClose}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider sx={{ my: 2, borderWidth: 2, color: '#B1B8BF' }} />
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: 'auto',
          px: 4,
          pt: 3,
          pb: 0,
          minHeight: 100,
        }}
      >
        {data?.currentLayerId === 11 &&
          userRole === 'Maintenance Admin' &&
          data?.workFlowId === 5 && (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Typography sx={{ marginBottom: 1 }}>
                Certificate of Completion Attachment
              </Typography>
              <Grid width={'45%'} marginTop={2}>
                <FormControl fullWidth>
                  <Button
                    variant="contained"
                    component="label"
                    sx={{ width: '60%' }}
                  >
                    <Box display="flex" alignItems="center">
                      Attach COC
                    </Box>
                    <input
                      type="file"
                      hidden
                      multiple
                      accept="application/pdf"
                      onChange={(e) => {
                        const files = e.target.files
                        if (files?.length) {
                          const firstFile = files[0]
                          handleUploadCOC(e, firstFile)
                        }
                      }}
                    />
                  </Button>
                </FormControl>

                {cocAttachments?.length > 0 && (
                  <>
                    {cocAttachments.map(
                      (
                        file:
                          | File
                          | {
                              fileType: string
                              fileName: string
                              url: string
                              presignedUrl: string
                            },
                        index: number,
                      ) => {
                        const isFileInstance = file instanceof File
                        const fileType = isFileInstance
                          ? file.type
                          : file.fileType
                        const fileName = isFileInstance
                          ? file.name
                          : file.fileName
                        const fileUrl = isFileInstance
                          ? URL.createObjectURL(file)
                          : file.presignedUrl

                        return (
                          <Grid
                            key={index}
                            container
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'flex-start',
                              marginTop: '1rem',
                            }}
                          >
                            {fileType?.startsWith('image/') ? (
                              <Box
                                display="flex"
                                flexDirection="row"
                                alignItems="center"
                                width={'100%'}
                                height={'50px'}
                              >
                                <Grid
                                  display="flex"
                                  flexDirection="row"
                                  alignItems="center"
                                  gap={1} 
                                >
                                  <img
                                    src={fileUrl}
                                    alt="Preview"
                                    width={25}
                                    height={25}
                                    style={{ objectFit: 'cover' }}
                                  />
                                  <Tooltip title={fileName} arrow>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        maxWidth: '120px',
                                        overflow: 'hidden',
                                        whiteSpace: 'nowrap',
                                        textOverflow: 'ellipsis',
                                        cursor: 'pointer',
                                      }}
                                    >
                                      <a
                                        target="_blank"
                                        href={fileUrl}
                                        style={{
                                          color: '#000',
                                          fontWeight: 500,
                                        }}
                                      >
                                        {fileName}
                                      </a>
                                    </Typography>
                                  </Tooltip>
                                </Grid>
                              </Box>
                            ) : (
                              <Box
                                display="flex"
                                flexDirection="row"
                                alignItems="center"
                                width={'100%'}
                                height={'50px'}
                              >
                                <Grid
                                  display="flex"
                                  flexDirection="row"
                                  alignItems="center"
                                  gap={1}
                                >
                                  <img
                                    src={PdfIcon}
                                    alt="PDF"
                                    width={25}
                                    height={25}
                                    style={{ objectFit: 'cover' }}
                                  />
                                  <Tooltip title={fileName} arrow>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        maxWidth: '120px',
                                        overflow: 'hidden',
                                        whiteSpace: 'nowrap',
                                        textOverflow: 'ellipsis',
                                        cursor: 'pointer',
                                      }}
                                    >
                                      <a
                                        target="_blank"
                                        href={fileUrl}
                                        style={{
                                          color: '#000',
                                          fontWeight: 500,
                                        }}
                                      >
                                        {fileName}
                                      </a>
                                    </Typography>
                                  </Tooltip>
                                  <IconButton
                                    onClick={() =>
                                      handleRemoveFile((file as any).id)
                                    }
                                    size="small"
                                  >
                                    <Delete />
                                  </IconButton>
                                </Grid>
                              </Box>
                            )}
                          </Grid>
                        )
                      },
                    )}
                  </>
                )}
              </Grid>

              <Box marginBottom={2} marginTop={2}>
                <Typography sx={{ marginBottom: 1 }}>
                  Project Completion Date
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={projectDate}
                    onChange={(newDate) => {
                      console.log('TESTING NEW DATE', newDate)
                      setProjectDate(newDate)
                    }}
                    maxDate={dayjs()}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        placeholder: 'Select project date',
                      },
                    }}
                  />
                </LocalizationProvider>

                <Button
                  variant="outlined"
                  sx={{ marginTop: 2 }}
                  onClick={() => {
                    setProjectDate(null)
                    mutateApprovalDateAndGatePass(
                      {
                        ...approvalDateAndGatePass,
                        dateCompletion: null,
                      },
                      {
                        optimisticData: {
                          ...approvalDateAndGatePass,
                          dateCompletion: null,
                        },
                        populateCache: true,
                        revalidate: false,
                      },
                    )
                  }}
                >
                  Reset Date
                </Button>
              </Box>
              <Box marginBottom={2}>
                <Typography sx={{ marginBottom: 1 }}>
                  Certificate of Completion Date
                </Typography>

                <DatePicker
                  value={cocDate}
                  onChange={(newDate) => setCocDate(newDate)}
                  maxDate={dayjs()}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      placeholder: 'Select COC date',
                    },
                  }}
                />

                <Button
                  variant="outlined"
                  sx={{ marginTop: 2 }}
                  onClick={() => {
                    setCocDate(null)
                    mutateApprovalDateAndGatePass(
                      {
                        ...approvalDateAndGatePass,
                        dateCocReceived: null,
                      },
                      {
                        optimisticData: {
                          ...approvalDateAndGatePass,
                          dateCocReceived: null,
                        },
                        populateCache: true,
                        revalidate: false,
                      },
                    )
                  }}
                >
                  Reset Date
                </Button>
              </Box>
            </LocalizationProvider>
          )}
            {(
              (data?.currentLayerId === 11 ||
                data?.currentLayerId === 17 ||
                data?.currentLayerId === 18) &&
              userRole === 'Maintenance Admin' &&
              data?.workFlowId != 5 &&
              (data?.status === 'FOR_COC_UPLOADING' ||
                data?.status === 'FOR_COC_DATE_AND_COMPLETION_DATE_INPUT')
            ) && (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Typography sx={{ marginBottom: 1 }}>
                Certificate of Completion Attachment
              </Typography>

              <Grid width={'45%'}>
                <FormControl fullWidth>
                  <Button
                    variant="contained"
                    component="label"
                    sx={{ width: '60%' }}
                  >
                    <Box display="flex" alignItems="center">
                      Attach COC
                    </Box>
                    <input
                      type="file"
                      hidden
                      multiple
                      accept="application/pdf"
                      onChange={(e) => {
                        const files = e.target.files
                        if (files?.length) {
                          const firstFile = files[0]
                          handleUploadCOC(e, firstFile)
                        }
                      }}
                    />
                  </Button>
                </FormControl>

                {cocAttachments?.length > 0 && (
                  <>
                    {cocAttachments.map(
                      (
                        file:
                          | File
                          | {
                              fileType: string
                              fileName: string
                              url: string
                              presignedUrl: string
                            },
                        index: number,
                      ) => {
                        const isFileInstance = file instanceof File
                        const fileType = isFileInstance
                          ? file.type
                          : file.fileType
                        const fileName = isFileInstance
                          ? file.name
                          : file.fileName
                        const fileUrl = isFileInstance
                          ? URL.createObjectURL(file)
                          : file.presignedUrl

                        return (
                          <Grid
                            key={index}
                            container
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'flex-start',
                              marginTop: '1rem',
                            }}
                          >
                            {fileType?.startsWith('image/') ? (
                              <Box
                                display="flex"
                                flexDirection="row"
                                alignItems="center"
                                width={'100%'}
                                height={'50px'}
                              >
                                <Grid
                                  display="flex"
                                  flexDirection="row"
                                  alignItems="center"
                                  gap={1}
                                >
                                  <img
                                    src={fileUrl}
                                    alt="Preview"
                                    width={25}
                                    height={25}
                                    style={{ objectFit: 'cover' }}
                                  />
                                  <Tooltip title={fileName} arrow>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        maxWidth: '120px',
                                        overflow: 'hidden',
                                        whiteSpace: 'nowrap',
                                        textOverflow: 'ellipsis',
                                        cursor: 'pointer',
                                      }}
                                    >
                                      <a
                                        target="_blank"
                                        href={fileUrl}
                                        style={{
                                          color: '#000',
                                          fontWeight: 500,
                                        }}
                                      >
                                        {fileName}
                                      </a>
                                    </Typography>
                                  </Tooltip>
                                </Grid>
                              </Box>
                            ) : (
                              <Box
                                display="flex"
                                flexDirection="row"
                                alignItems="center"
                                width={'100%'}
                                height={'50px'}
                              >
                                <Grid
                                  display="flex"
                                  flexDirection="row"
                                  alignItems="center"
                                  gap={1}
                                >
                                  <img
                                    src={PdfIcon}
                                    alt="PDF"
                                    width={25}
                                    height={25}
                                    style={{ objectFit: 'cover' }}
                                  />
                                  <Tooltip title={fileName} arrow>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        maxWidth: '120px',
                                        overflow: 'hidden',
                                        whiteSpace: 'nowrap',
                                        textOverflow: 'ellipsis',
                                        cursor: 'pointer',
                                      }}
                                    >
                                      <a
                                        target="_blank"
                                        href={fileUrl}
                                        style={{
                                          color: '#000',
                                          fontWeight: 500,
                                        }}
                                      >
                                        {fileName}
                                      </a>
                                    </Typography>
                                  </Tooltip>
                                  <IconButton
                                    onClick={() =>
                                      handleRemoveFile((file as any).id)
                                    }
                                    size="small"
                                  >
                                    <Delete />
                                  </IconButton>
                                </Grid>
                              </Box>
                            )}
                          </Grid>
                        )
                      },
                    )}
                  </>
                )}
              </Grid>

              <Box marginBottom={2} marginTop={2}>
                <Typography sx={{ marginBottom: 1 }}>
                  Project Completion Date 
                </Typography>

                <DatePicker
                  value={projectDate}
                  onChange={(newDate) => setProjectDate(newDate)}
                  maxDate={dayjs()}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      placeholder: 'Select project date',
                    },
                  }}
                />

                <Button
                  variant="outlined"
                  sx={{ marginTop: 2 }}
                  onClick={() => {
                    setProjectDate(null)
                  }}
                >
                  Reset Date
                </Button>
              </Box>
              <Box marginBottom={2}>
                <Typography sx={{ marginBottom: 1 }}>
                  Certificate of Completion Date
                </Typography>

                <DatePicker
                  value={cocDate}
                  onChange={(newDate) => setCocDate(newDate)}
                  maxDate={dayjs()}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      placeholder: 'Select COC date',
                    },
                  }}
                />

                <Button
                  variant="outlined"
                  sx={{ marginTop: 2 }}
                  onClick={() => {
                    setCocDate(null)
                  }}
                >
                  Reset Date
                </Button>
              </Box>
            </LocalizationProvider>
          )}
        {/* Conditional DatePicker */}
        {/* {((data.workFlowId === 3 && data?.currentLayerId === 17) ||
            (data.workFlowId === 4 && data.currentLayerId === 18)) && (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box marginBottom={2} marginTop={2}>
                <Typography sx={{ marginBottom: 1 }}>
                  Project Completion Date
                </Typography>

                <DatePicker
                  value={projectDate}
                  onChange={(newDate) => setProjectDate(newDate)}
                  maxDate={dayjs()}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      placeholder: 'Select project date',
                    },
                  }}
                />

                <Button
                  variant="outlined"
                  sx={{ marginTop: 2 }}
                  onClick={() => {
                    setProjectDate(null)
                  }}
                >
                  Reset Date
                </Button>
              </Box>
              <Box marginBottom={2}>
                <Typography sx={{ marginBottom: 1 }}>
                  Certificate of Completion Date
                </Typography>

                <DatePicker
                  value={cocDate}
                  onChange={(newDate) => setCocDate(newDate)}
                  maxDate={dayjs()}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      placeholder: 'Select COC date',
                    },
                  }}
                />

                <Button
                  variant="outlined"
                  sx={{ marginTop: 2 }}
                  onClick={() => {
                    setCocDate(null)
                  }}
                >
                  Reset Date
                </Button>
              </Box>
            </LocalizationProvider>
          )} */}
        {shouldFetchReplyAttachments && (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Grid width={'80%'} marginTop={2}>
              <FormControl fullWidth>
                <Typography component="span" sx={{ my: 1 }}>
                  Attachment of Reply Letter
                  <Typography component="span" color="red" ml={0.5}>
                    *
                  </Typography>
                </Typography>
                <Button
                  variant="contained"
                  component="label"
                  sx={{ maxWidth: '40%' }}
                >
                  <Box display="flex" alignItems="center">
                    Attach
                  </Box>
                  <input
                    type="file"
                    hidden
                    multiple
                    accept="application/pdf"
                    onChange={(e) => {
                      const files = e.target.files
                      if (files?.length) {
                        const firstFile = files[0]
                        handleUploadReply(e, firstFile)
                      }
                    }}
                  />
                </Button>
              </FormControl>

              {replyAttachments?.length > 0 && (
                <>
                  {replyAttachments
                    .filter(({ attachmentType }) => {
                      return attachmentType === 'REPLY_LETTER_QUOTATION'
                    })
                    .map(
                      (
                        file:
                          | File
                          | {
                              fileType: string
                              fileName: string
                              url: string
                              attachmentType: string
                              id: number
                              presignedUrl: string
                            },
                        index: number,
                      ) => {
                        const isFileInstance = file instanceof File
                        const fileType = isFileInstance
                          ? file.type
                          : file.fileType
                        const fileName = isFileInstance
                          ? file.name
                          : file.fileName
                        const fileUrl = isFileInstance
                          ? URL.createObjectURL(file)
                          : file.presignedUrl

                        return (
                          <Grid
                            key={index}
                            container
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'flex-start',
                              marginTop: '1rem',
                            }}
                          >
                            {fileType?.startsWith('image/') ? (
                              <Box
                                display="flex"
                                flexDirection="row"
                                alignItems="center"
                                width={'100%'}
                                height={'50px'}
                              >
                                <Grid
                                  display="flex"
                                  flexDirection="row"
                                  alignItems="center"
                                  gap={1}
                                  padding={1}
                                  borderRadius={
                                    file.attachmentType === 'QUOTATION'
                                      ? 2.5
                                      : 0
                                  }
                                >
                                  <img
                                    src={ImageIcon}
                                    alt="PDF"
                                    width={25}
                                    height={25}
                                    style={{ objectFit: 'cover' }}
                                  />
                                  <Grid
                                    display={'flex'}
                                    flexDirection={'column'}
                                    alignItems={'flex-start'}
                                    justifyContent={'flex-start'}
                                    sx={{ cursor: 'pointer' }}
                                  >
                                    <Tooltip title={fileName} arrow>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          maxWidth: '120px',
                                          overflow: 'hidden',
                                          whiteSpace: 'nowrap',
                                          textOverflow: 'ellipsis',
                                          cursor: 'pointer',
                                        }}
                                      >
                                        <a
                                          target="_blank"
                                          href={fileUrl}
                                          style={{
                                            color: '#000',
                                            fontWeight: 500,
                                          }}
                                        >
                                          {fileName}
                                        </a>
                                      </Typography>
                                    </Tooltip>
                                  </Grid>
                                  {file.attachmentType !== 'QUOTATION' && (
                                    <IconButton
                                      onClick={() =>
                                        handleRemoveReplyFile((file as any).id)
                                      }
                                      size="small"
                                    >
                                      <Delete />
                                    </IconButton>
                                  )}
                                </Grid>
                              </Box>
                            ) : (
                              <Box
                                display="flex"
                                flexDirection="row"
                                alignItems="center"
                                width={'100%'}
                                height={'50px'}
                              >
                                <Grid
                                  display="flex"
                                  flexDirection="row"
                                  alignItems="center"
                                  gap={1}
                                  padding={1}
                                  borderRadius={
                                    file.attachmentType === 'QUOTATION'
                                      ? 2.5
                                      : 0
                                  }
                                >
                                  <img
                                    src={PdfIcon}
                                    alt="PDF"
                                    width={25}
                                    height={25}
                                    style={{ objectFit: 'cover' }}
                                  />
                                  <Grid
                                    display={'flex'}
                                    flexDirection={'column'}
                                    alignItems={'flex-start'}
                                    justifyContent={'flex-start'}
                                  >
                                    <Tooltip title={fileName} arrow>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          maxWidth: '120px',
                                          overflow: 'hidden',
                                          whiteSpace: 'nowrap',
                                          textOverflow: 'ellipsis',
                                          cursor: 'pointer',
                                        }}
                                      >
                                        <a
                                          target="_blank"
                                          href={fileUrl}
                                          style={{
                                            color: '#000',
                                            fontWeight: 500,
                                          }}
                                        >
                                          {fileName}
                                        </a>
                                      </Typography>
                                    </Tooltip>
                                  </Grid>
                                  {file.attachmentType !== 'QUOTATION' && (
                                    <IconButton
                                      onClick={() =>
                                        handleRemoveReplyFile((file as any).id)
                                      }
                                      size="small"
                                    >
                                      <Delete />
                                    </IconButton>
                                  )}
                                </Grid>
                              </Box>
                            )}
                          </Grid>
                        )
                      },
                    )}
                </>
              )}
            </Grid>
          </LocalizationProvider>
        )}
        {shouldFetchReplyAttachments && (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Grid width={'80%'} marginTop={2}>
              <FormControl fullWidth>
                <Typography>Quotation(s)</Typography>
              </FormControl>

              {replyAttachments?.length > 0 && (
                <>
                  {replyAttachments
                    .filter(({ attachmentType }) => {
                      return attachmentType === 'QUOTATION'
                    })
                    .map(
                      (
                        file:
                          | File
                          | {
                              fileType: string
                              fileName: string
                              url: string
                              attachmentType: string
                              id: number
                              presignedUrl: string
                            },
                        index: number,
                      ) => {
                        const isFileInstance = file instanceof File
                        const fileType = isFileInstance
                          ? file.type
                          : file.fileType
                        const fileName = isFileInstance
                          ? file.name
                          : file.fileName
                        const fileUrl = isFileInstance
                          ? URL.createObjectURL(file)
                          : file.presignedUrl

                        return (
                          <Grid
                            key={index}
                            container
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'flex-start',
                              marginTop: '1rem',
                            }}
                          >
                            {fileType?.startsWith('image/') ? (
                              <Box
                                display="flex"
                                flexDirection="row"
                                alignItems="center"
                                width={'100%'}
                                height={'50px'}
                              >
                                <Grid
                                  display="flex"
                                  flexDirection="row"
                                  alignItems="center"
                                  gap={1}
                                  padding={1}
                                  borderRadius={
                                    file.attachmentType === 'QUOTATION'
                                      ? 2.5
                                      : 0
                                  }
                                >
                                  <img
                                    src={ImageIcon}
                                    alt="PDF"
                                    width={25}
                                    height={25}
                                    style={{ objectFit: 'cover' }}
                                  />
                                  <Grid
                                    display={'flex'}
                                    flexDirection={'column'}
                                    alignItems={'flex-start'}
                                    justifyContent={'flex-start'}
                                    sx={{ cursor: 'pointer' }}
                                  >
                                    <Tooltip title={fileName} arrow>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          maxWidth: '120px',
                                          overflow: 'hidden',
                                          whiteSpace: 'nowrap',
                                          textOverflow: 'ellipsis',
                                          cursor: 'pointer',
                                        }}
                                      >
                                        <a
                                          target="_blank"
                                          href={fileUrl}
                                          style={{
                                            color: '#000',
                                            fontWeight: 500,
                                          }}
                                        >
                                          {fileName}
                                        </a>
                                      </Typography>
                                    </Tooltip>
                                  </Grid>
                                  {file.attachmentType !== 'QUOTATION' && (
                                    <IconButton
                                      onClick={() =>
                                        handleRemoveReplyFile((file as any).id)
                                      }
                                      size="small"
                                    >
                                      <Delete />
                                    </IconButton>
                                  )}
                                </Grid>
                              </Box>
                            ) : (
                              <Box
                                display="flex"
                                flexDirection="row"
                                alignItems="center"
                                width={'100%'}
                                height={'50px'}
                              >
                                <Grid
                                  display="flex"
                                  flexDirection="row"
                                  alignItems="center"
                                  gap={1}
                                  padding={1}
                                  borderRadius={
                                    file.attachmentType === 'QUOTATION'
                                      ? 2.5
                                      : 0
                                  }
                                >
                                  <img
                                    src={PdfIcon}
                                    alt="PDF"
                                    width={25}
                                    height={25}
                                    style={{ objectFit: 'cover' }}
                                  />
                                  <Grid
                                    display={'flex'}
                                    flexDirection={'column'}
                                    alignItems={'flex-start'}
                                    justifyContent={'flex-start'}
                                  >
                                    <Tooltip title={fileName} arrow>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          maxWidth: '120px',
                                          overflow: 'hidden',
                                          whiteSpace: 'nowrap',
                                          textOverflow: 'ellipsis',
                                          cursor: 'pointer',
                                        }}
                                      >
                                        <a
                                          target="_blank"
                                          href={fileUrl}
                                          style={{
                                            color: '#000',
                                            fontWeight: 500,
                                          }}
                                        >
                                          {fileName}
                                        </a>
                                      </Typography>
                                    </Tooltip>
                                  </Grid>
                                  {file.attachmentType !== 'QUOTATION' && (
                                    <IconButton
                                      onClick={() =>
                                        handleRemoveReplyFile((file as any).id)
                                      }
                                      size="small"
                                    >
                                      <Delete />
                                    </IconButton>
                                  )}
                                </Grid>
                              </Box>
                            )}
                          </Grid>
                        )
                      },
                    )}
                </>
              )}
            </Grid>
          </LocalizationProvider>
        )}
        {shouldFetchDISAttachments && (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Grid width={'80%'} marginTop={2}>
              <FormControl fullWidth>
                <Typography mb={1}>
                  Attached Dealer Investment Summary
                  <Typography component="span" color="red" ml={0.5}>
                    *
                  </Typography>
                </Typography>
                <Button
                  variant="contained"
                  component="label"
                  sx={{ width: '70%', maxWidth: '40%' }}
                >
                  <Box display="flex" alignItems="center">
                    Attach
                  </Box>
                  <input
                    type="file"
                    hidden
                    multiple
                    accept="application/pdf"
                    onChange={(e) => {
                      const files = e.target.files
                      if (files?.length) {
                        const firstFile = files[0]
                        handleUploadDIS(e, firstFile)
                      }
                    }}
                  />
                </Button>
              </FormControl>

              {disAttachments?.length > 0 && (
                <>
                  {disAttachments?.map(
                    (
                      file:
                        | File
                        | {
                            fileType: string
                            fileName: string
                            url: string
                            presignedUrl: string
                          },
                      index: number,
                    ) => {
                      const isFileInstance = file instanceof File
                      const fileType = isFileInstance
                        ? file.type
                        : file.fileType
                      const fileName = isFileInstance
                        ? file.name
                        : file.fileName
                      const fileUrl = isFileInstance
                        ? URL.createObjectURL(file)
                        : file.presignedUrl

                      return (
                        <Grid
                          key={index}
                          container
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            marginTop: '1rem',
                          }}
                        >
                          {fileType?.startsWith('image/') ? (
                            <Box
                              display="flex"
                              flexDirection="row"
                              alignItems="center"
                              width={'100%'}
                              height={'50px'}
                            >
                              <Grid
                                display="flex"
                                flexDirection="row"
                                alignItems="center"
                                gap={1}
                              >
                                <img
                                  src={fileUrl}
                                  alt="Preview"
                                  width={25}
                                  height={25}
                                  style={{ objectFit: 'cover' }}
                                />
                                <Tooltip title={fileName} arrow>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      maxWidth: '120px',
                                      overflow: 'hidden',
                                      whiteSpace: 'nowrap',
                                      textOverflow: 'ellipsis',
                                      cursor: 'pointer',
                                    }}
                                  >
                                    <a
                                      target="_blank"
                                      href={fileUrl}
                                      style={{
                                        color: '#000',
                                        fontWeight: 500,
                                      }}
                                    >
                                      {fileName}
                                    </a>
                                  </Typography>
                                </Tooltip>
                              </Grid>
                            </Box>
                          ) : (
                            <Box
                              display="flex"
                              flexDirection="row"
                              alignItems="center"
                              width={'100%'}
                              height={'50px'}
                            >
                              <Grid
                                display="flex"
                                flexDirection="row"
                                alignItems="center"
                                gap={1}
                              >
                                <img
                                  src={PdfIcon}
                                  alt="PDF"
                                  width={25}
                                  height={25}
                                  style={{ objectFit: 'cover' }}
                                />
                                <Tooltip title={fileName} arrow>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      maxWidth: '120px',
                                      overflow: 'hidden',
                                      whiteSpace: 'nowrap',
                                      textOverflow: 'ellipsis',
                                      cursor: 'pointer',
                                    }}
                                  >
                                    <a
                                      target="_blank"
                                      href={fileUrl}
                                      style={{
                                        color: '#000',
                                        fontWeight: 500,
                                      }}
                                    >
                                      {fileName}
                                    </a>
                                  </Typography>
                                </Tooltip>
                                <IconButton
                                  onClick={() =>
                                    handleRemoveDISFile((file as any).id)
                                  }
                                  size="small"
                                >
                                  <Delete />
                                </IconButton>
                              </Grid>
                            </Box>
                          )}
                        </Grid>
                      )
                    },
                  )}
                </>
              )}
            </Grid>
          </LocalizationProvider>
        )}
        {shouldFetchSignedDISAttachments && (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Grid width={'80%'}>
              <FormControl fullWidth>
                <Typography mb={1}>
                  Attached Signed Dealer Investment Summary
                  <Typography component="span" color="red" ml={0.5}>
                    *
                  </Typography>
                </Typography>

                <Button
                  variant="contained"
                  component="label"
                  sx={{ width: '70%', maxWidth: '40%' }}
                >
                  <Box display="flex" alignItems="center">
                    Attach
                  </Box>
                  <input
                    type="file"
                    hidden
                    multiple
                    accept="application/pdf"
                    onChange={(e) => {
                      const files = e.target.files
                      if (files?.length) {
                        const firstFile = files[0]
                        handleUploadSignedDIS(e, firstFile)
                      }
                    }}
                  />
                </Button>
              </FormControl>

              {signedDisAttachments?.length > 0 && (
                <>
                  {signedDisAttachments.map(
                    (
                      file:
                        | File
                        | {
                            fileType: string
                            fileName: string
                            url: string
                            presignedUrl: string
                          },
                      index: number,
                    ) => {
                      const isFileInstance = file instanceof File
                      const fileType = isFileInstance
                        ? file.type
                        : file.fileType
                      const fileName = isFileInstance
                        ? file.name
                        : file.fileName
                      const fileUrl = isFileInstance
                        ? URL.createObjectURL(file)
                        : file.presignedUrl

                      return (
                        <Grid
                          key={index}
                          container
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            marginTop: '1rem',
                          }}
                        >
                          {fileType?.startsWith('image/') ? (
                            <Box
                              display="flex"
                              flexDirection="row"
                              alignItems="center"
                              width={'100%'}
                              height={'50px'}
                            >
                              <Grid
                                display="flex"
                                flexDirection="row"
                                alignItems="center"
                                gap={1}
                              >
                                <img
                                  src={fileUrl}
                                  alt="Preview"
                                  width={25}
                                  height={25}
                                  style={{ objectFit: 'cover' }}
                                />
                                <Tooltip title={fileName} arrow>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      maxWidth: '120px',
                                      overflow: 'hidden',
                                      whiteSpace: 'nowrap',
                                      textOverflow: 'ellipsis',
                                      cursor: 'pointer',
                                    }}
                                  >
                                    <a
                                      target="_blank"
                                      href={fileUrl}
                                      style={{
                                        color: '#000',
                                        fontWeight: 500,
                                      }}
                                    >
                                      {fileName}
                                    </a>
                                  </Typography>
                                </Tooltip>
                              </Grid>
                            </Box>
                          ) : (
                            <Box
                              display="flex"
                              flexDirection="row"
                              alignItems="center"
                              width={'100%'}
                              height={'50px'}
                            >
                              <Grid
                                display="flex"
                                flexDirection="row"
                                alignItems="center"
                                gap={1}
                              >
                                <img
                                  src={PdfIcon}
                                  alt="PDF"
                                  width={25}
                                  height={25}
                                  style={{ objectFit: 'cover' }}
                                />
                                <Tooltip title={fileName} arrow>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      maxWidth: '120px',
                                      overflow: 'hidden',
                                      whiteSpace: 'nowrap',
                                      textOverflow: 'ellipsis',
                                      cursor: 'pointer',
                                    }}
                                  >
                                    <a
                                      target="_blank"
                                      href={fileUrl}
                                      style={{
                                        color: '#000',
                                        fontWeight: 500,
                                      }}
                                    >
                                      {fileName}
                                    </a>
                                  </Typography>
                                </Tooltip>
                                <IconButton
                                  onClick={() =>
                                    handleRemoveSignedDISFile((file as any).id)
                                  }
                                  size="small"
                                >
                                  <Delete />
                                </IconButton>
                              </Grid>
                            </Box>
                          )}
                        </Grid>
                      )
                    },
                  )}
                </>
              )}
            </Grid>
            <Grid width={'100%'}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Typography sx={{ marginBottom: 1, marginTop: 1 }}>Approval Date 
                  <Typography component="span" color="red" ml={0.5}>
                    *
                  </Typography></Typography>
                <DatePicker
                  label="Date of Approval"
                  value={approvalDate}
                  onChange={(newValue) => setApprovalDate(newValue)}
                  slotProps={{ textField: { fullWidth: true }  }}
                />
              </LocalizationProvider>
            </Grid>
          </LocalizationProvider>
        )}
        <Box sx={{ flex: 1 }} /> {/* This will push the remarks to bottom */}
        <Box
          sx={{
            paddingTop: 2,
            marginTop: 'auto', // This ensures it stays at bottom
          }}
        >
          <Typography sx={{ marginBottom: 1 }}>Remarks</Typography>
          <CustomTextField
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            fullWidth
            multiline
            maxRows={3}
            variant="outlined"
            placeholder="Enter remarks here..."
          />
        </Box>
      </DialogContent>

      {/* Action Buttons */}
      <ButtonGroup>
        <Button
          variant="outlined"
          sx={{
            borderColor: 'black',
            color: 'black',
            width: {
              xs: '100%',
              sm: 'auto',
            },
            '&:hover': {
              borderColor: 'black',
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
            },
          }}
          onClick={onClose}
        >
          Cancel
        </Button>
        {shouldShowUpdate ? (
          <Button
            variant="contained"
            disabled={isApproving}
            sx={{
              backgroundColor: '#379F86',
              color: '#ffffff',
              width: {
                xs: '100%',
                sm: 'auto',
              },
              opacity: isApproving ? 0.6 : 1,
            }}
            onClick={async () => {
              if (isApproving) return // Prevent multiple calls
              setIsApproving(true)
              try {
                await onApprove(
                  data.id,
                  remarks,
                  cocDate,
                  projectDate,
                  shouldShowUpdate,
                )
              } catch (error) {
                console.error('Failed to update:', error)
                handleAlertMessage('Failed to update. Please try again.', 'error')
              } finally {
                setIsApproving(false)
              }
            }}
          >
            {isApproving ? 'Updating...' : 'Update'}
          </Button>
        ) : (
          <Button
            variant="contained"
            disabled={isApproving}
            sx={{
              backgroundColor: '#379F86',
              color: '#ffffff',
              opacity: isApproving ? 0.6 : 1,
            }}
            onClick={async () => {
              if (isApproving) return // Prevent multiple calls
              
              if (shouldFetchSignedDISAttachments) {
                if (!signedDisAttachments) {
                  return handleAlertMessage(
                    'Signed DIS Attachments is required',
                    'error',
                  )
                } else {
                  handleApproveWorkflow4Validation12()
                }
              } else if (shouldFetchReplyAttachments) {
                if (!replyAttachments) {
                  return handleAlertMessage(
                    'Reply Attachments is required',
                    'error',
                  )
                } else {
                  setIsApproving(true)
                  try {
                    await onApprove(data.id, remarks, cocDate, projectDate)
                  } catch (error) {
                    console.error('Failed to approve:', error)
                    handleAlertMessage('Failed to approve. Please try again.', 'error')
                  } finally {
                    setIsApproving(false)
                  }
                }
              } else if (shouldFetchDISAttachments) {
                if (!disAttachments) {
                  return handleAlertMessage(
                    'DIS Attachments is required',
                    'error',
                  )
                } else {
                  setIsApproving(true)
                  try {
                    await onApprove(data.id, remarks, cocDate, projectDate)
                  } catch (error) {
                    console.error('Failed to approve:', error)
                    handleAlertMessage('Failed to approve. Please try again.', 'error')
                  } finally {
                    setIsApproving(false)
                  }
                }
              } else {
                setIsApproving(true)
                try {
                  await onApprove(data.id, remarks, cocDate, projectDate)
                } catch (error) {
                  console.error('Failed to approve:', error)
                  handleAlertMessage('Failed to approve. Please try again.', 'error')
                } finally {
                  setIsApproving(false)
                }
              }
            }}
          >
            {isApproving ? 'Approving...' : 'Approve'}
          </Button>
        )}
      </ButtonGroup>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={alertRest.showAlert}
        onClose={alertRest.handleCloseAlert}
        autoHideDuration={3000}
      >
        <Alert
          onClose={alertRest.handleCloseAlert}
          elevation={6}
          variant="filled"
          severity={alertRest.alertSeverity}
        >
          {alertRest.alertMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  )
}
